/**
 * @fileoverview Rapprochement financier.
 *
 * Vue UI mockée du dernier rapprochement entre paiements confirmés et
 * enregistrements STI. La liste des lots est filtrable (statut + plage de
 * dates de génération) au style de la toolbar de `PaymentsPage`, tandis que
 * les métriques et l'export CSV restent ancrés sur le dernier lot.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Amount,
  Button,
  DataCard,
  DataTable,
  EmptyState,
  FormField,
  Input,
  MetricCard,
  PageHeader,
  ReasonTextarea,
  SectionHeader,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  StatusBadge,
} from '@gnpc/ui';
import { CheckCircle2, Download, Play, Scale, TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';
import {
  ADMIN_PAYMENTS,
  RECONCILIATION_BATCHES,
  formatDateTime,
  operatorLabel,
  type AdminPayment,
  type ReconciliationBatch,
} from '../data/backoffice.ts';
import { CapabilityGuard } from '../features/auth/CapabilityProvider.tsx';

const columns: ColumnDef<ReconciliationBatch>[] = [
  { accessorKey: 'period', header: 'Période' },
  { accessorKey: 'matchedCount', header: 'Matchés' },
  { accessorKey: 'unmatchedCount', header: 'Écarts' },
  { accessorKey: 'matchedAmount', header: 'Montant matché', cell: ({ row }) => <Amount value={row.original.matchedAmount} /> },
  { accessorKey: 'unmatchedAmount', header: 'Montant écart', cell: ({ row }) => <Amount value={row.original.unmatchedAmount} /> },
  {
    accessorKey: 'status',
    header: 'Statut',
    cell: ({ row }) =>
      row.original.status === 'OK' ? (
        <StatusBadge kind="integration" status="UP" compact />
      ) : (
        <StatusBadge kind="integration" status="DEGRADED" compact />
      ),
  },
  { accessorKey: 'generatedAt', header: 'Généré le', cell: ({ row }) => formatDateTime(row.original.generatedAt) },
];

function csvCell(value: string | number): string {
  const text = String(value);
  return /[;"\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function downloadReconciliationCsv(batch: ReconciliationBatch, discrepancies: AdminPayment[]): void {
  const rows: Array<Array<string | number>> = [
    ['Rapprochement', batch.id],
    ['Période', batch.period],
    ['Statut', batch.status === 'OK' ? 'Opérationnel' : 'Dégradé'],
    ['Transactions rapprochées', batch.matchedCount],
    ['Écarts', batch.unmatchedCount],
    ['Montant rapproché', batch.matchedAmount],
    ['Montant en écart', batch.unmatchedAmount],
    ['Généré le', formatDateTime(batch.generatedAt)],
    [],
    ['Référence', 'Plaque', 'Opérateur', 'Lignes', 'Enregistrement Quipux', 'Montant'],
    ...discrepancies.map((payment) => [
      payment.merchantReference,
      payment.plate,
      operatorLabel(payment.operatorCode),
      payment.lineItems.length,
      payment.sgiStatus,
      payment.totalAmount,
    ]),
  ];
  const csv = rows.map((row) => row.map(csvCell).join(';')).join('\n');
  const blob = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${batch.id}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Filtres appliqués à la liste des lots de rapprochement.
 *
 * `statusFilter` accepte `ALL` (aucun filtre) ou l'un des statuts du contrat
 * (`OK` / `REVIEW_REQUIRED`). `dateFrom` / `dateTo` sont des dates ISO
 * (`AAAA-MM-JJ`) bornant la date de génération du lot.
 */
interface ReconciliationFilters {
  statusFilter: string;
  dateFrom: string;
  dateTo: string;
}

/** État neutre des filtres — sert d'initialisation et de cible au reset. */
const INITIAL_FILTERS: ReconciliationFilters = {
  statusFilter: 'ALL',
  dateFrom: '',
  dateTo: '',
};

export function ReconciliationPage(): JSX.Element {
  const latest = RECONCILIATION_BATCHES[0];
  const [period, setPeriod] = useState('2026-07-14');
  const [source, setSource] = useState('Paiements confirmés');
  const [threshold, setThreshold] = useState('1000');
  /** Périmètre opérateur du rapprochement (C-24). */
  const [operatorScope, setOperatorScope] = useState('ALL');
  /** Récurrence : ponctuel ou planifié (C-23). */
  const [recurrence, setRecurrence] = useState('MANUAL');
  /** Annotation / justification exigée avant clôture d'un écart (C-22). */
  const [annotation, setAnnotation] = useState('');
  const [selectedBatchId, setSelectedBatchId] = useState(latest?.id ?? '');
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [prepareSheetOpen, setPrepareSheetOpen] = useState(false);

  // Filtres de la liste des lots : saisis dans `draftFilters`, appliqués sur
  // action explicite dans `appliedFilters` (même modèle que PaymentsPage —
  // la recherche ne se déclenche pas à chaque frappe).
  const [draftFilters, setDraftFilters] = useState<ReconciliationFilters>(INITIAL_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<ReconciliationFilters>(INITIAL_FILTERS);

  const updateDraftFilter = <K extends keyof ReconciliationFilters>(
    key: K,
    value: ReconciliationFilters[K],
  ) => {
    setDraftFilters((current) => ({ ...current, [key]: value }));
  };

  /**
   * Lots visibles après application des filtres.
   *
   * La plage de dates porte sur `generatedAt` (date de génération du lot) et
   * non sur `period` : ce dernier est un libellé humain (« 14 juillet 2026 »)
   * non triable/comparable. On compare donc la date ISO de génération, comme
   * PaymentsPage le fait sur `createdAt`.
   */
  const filteredBatches = useMemo(() => {
    return RECONCILIATION_BATCHES.filter((batch) => {
      const matchesStatus =
        appliedFilters.statusFilter === 'ALL' || batch.status === appliedFilters.statusFilter;
      const batchDate = batch.generatedAt.slice(0, 10);
      const matchesDate =
        (!appliedFilters.dateFrom || batchDate >= appliedFilters.dateFrom) &&
        (!appliedFilters.dateTo || batchDate <= appliedFilters.dateTo);
      return matchesStatus && matchesDate;
    });
  }, [appliedFilters]);

  const applyFilters = () => {
    setAppliedFilters(draftFilters);
  };

  const resetFilters = () => {
    setDraftFilters(INITIAL_FILTERS);
    setAppliedFilters(INITIAL_FILTERS);
  };

  const discrepancies = useMemo(
    () =>
      ADMIN_PAYMENTS.filter(
        (payment) =>
          payment.paymentStatus === 'PAID' &&
          (payment.sgiStatus === 'FAILED' ||
            payment.sgiStatus === 'PARTIAL' ||
            payment.sgiStatus === 'PENDING'),
      ),
    [],
  );

  if (!latest) {
    return (
      <EmptyState
        title="Aucun rapprochement"
        description="Les rapprochements apparaîtront ici."
      />
    );
  }

  const selectedBatch = RECONCILIATION_BATCHES.find((batch) => batch.id === selectedBatchId) ?? latest;
  const selectedBatchDiscrepancies =
    selectedBatch.status === 'REVIEW_REQUIRED' ? discrepancies : [];
  const latestDiscrepancies = latest.status === 'REVIEW_REQUIRED' ? discrepancies : [];

  return (
    <>
      <div className="grid gap-5">
        <PageHeader
          eyebrow="Rapprochement"
          title="Contrôler les écarts financiers"
          description="Comparer les paiements confirmés avec les enregistrements Quipux et les relevés opérateurs, et identifier les écarts à traiter."
          actions={
            <>
              <Button variant="outline" onClick={() => downloadReconciliationCsv(latest, latestDiscrepancies)}>
                <Download className="size-4" />
                Télécharger CSV
              </Button>
              <Sheet open={prepareSheetOpen} onOpenChange={setPrepareSheetOpen}>
                <SheetTrigger asChild>
                  <Button>
                    <Play className="size-4" />
                    Préparer
                  </Button>
                </SheetTrigger>
                <SheetContent className="w-[calc(100vw-2rem)] overflow-y-auto p-0 sm:max-w-md">
                  <SheetHeader className="border-border border-b p-5 pr-12">
                    <SheetTitle>Préparer un rapprochement</SheetTitle>
                    <SheetDescription>
                      Configurer le périmètre avant de lancer le rapprochement.
                    </SheetDescription>
                  </SheetHeader>

                  <div className="grid gap-4 p-5">
                    <FormField label="Période" required>
                      <Input type="date" value={period} onChange={(event) => setPeriod(event.target.value)} />
                    </FormField>
                    <FormField label="Source" required>
                      <select
                        value={source}
                        onChange={(event) => setSource(event.target.value)}
                        className="border-input bg-background h-10 w-full rounded-md border px-3 text-sm"
                      >
                        <option value="Paiements confirmés">Paiements confirmés</option>
                        <option value="Remboursements approuvés">Remboursements approuvés</option>
                        <option value="Écritures Quipux">Écritures Quipux</option>
                        <option value="Relevés de règlement opérateurs">
                          Relevés de règlement opérateurs
                        </option>
                      </select>
                    </FormField>
                    <FormField label="Tolérance d'écart" required>
                      <Input
                        value={threshold}
                        onChange={(event) => setThreshold(event.target.value)}
                        inputMode="numeric"
                      />
                    </FormField>
                    {/* Périmètre opérateur (C-24) — rapprochement ciblé par opérateur. */}
                    <FormField label="Périmètre opérateur">
                      <select
                        value={operatorScope}
                        onChange={(event) => setOperatorScope(event.target.value)}
                        className="border-input bg-background h-10 w-full rounded-md border px-3 text-sm"
                      >
                        <option value="ALL">Tous les opérateurs</option>
                        <option value="OM_CI">Orange Money</option>
                        <option value="MOOV_CI">Moov Money</option>
                        <option value="MTN_CI">MTN Money</option>
                        <option value="WAVE_CI">Wave</option>
                      </select>
                    </FormField>
                    {/* Récurrence (C-23) — rapprochement automatique planifié. */}
                    <FormField label="Récurrence">
                      <select
                        value={recurrence}
                        onChange={(event) => setRecurrence(event.target.value)}
                        className="border-input bg-background h-10 w-full rounded-md border px-3 text-sm"
                      >
                        <option value="MANUAL">Manuel (ponctuel)</option>
                        <option value="DAILY">Quotidien (planifié)</option>
                        <option value="WEEKLY">Hebdomadaire (planifié)</option>
                      </select>
                    </FormField>
                  </div>

                  <SheetFooter className="border-border border-t p-5">
                    <CapabilityGuard capability="reconciliation:run">
                      <SheetClose asChild>
                        <Button
                          onClick={() =>
                            toast.success(
                              recurrence === 'MANUAL'
                                ? 'Rapprochement lancé'
                                : 'Rapprochement planifié',
                              {
                                description: `${source} — ${
                                  recurrence === 'MANUAL'
                                    ? `période du ${period}`
                                    : recurrence === 'DAILY'
                                      ? 'exécution quotidienne planifiée'
                                      : 'exécution hebdomadaire planifiée'
                                }.`,
                              },
                            )
                          }
                        >
                          <Play className="size-4" />
                          {recurrence === 'MANUAL'
                            ? 'Lancer le rapprochement'
                            : 'Planifier le rapprochement'}
                        </Button>
                      </SheetClose>
                    </CapabilityGuard>
                    <SheetClose asChild>
                      <Button variant="outline">Annuler</Button>
                    </SheetClose>
                  </SheetFooter>
                </SheetContent>
              </Sheet>
            </>
          }
        />

        <section className="grid gap-4 md:grid-cols-3">
          <MetricCard
            label="Montant rapproché"
            value={<Amount value={latest.matchedAmount} compact />}
            icon={<CheckCircle2 />}
            tone="success"
          />
          <MetricCard
            label="Écart à revoir"
            value={<Amount value={latest.unmatchedAmount} compact />}
            icon={<TriangleAlert />}
            tone={latest.unmatchedAmount > 0 ? 'danger' : 'success'}
          />
          <MetricCard
            label="Dernier rapprochement"
            value={latest.period}
            icon={<Scale />}
            tone="info"
          />
        </section>

        <DataTable
          columns={columns}
          data={filteredBatches}
          toolbar={
            <form
              className="grid w-full items-end gap-3 sm:grid-cols-2 lg:grid-cols-3"
              onSubmit={(event) => {
                event.preventDefault();
                applyFilters();
              }}
            >
              <FormField label="Statut">
                <select
                  value={draftFilters.statusFilter}
                  onChange={(event) => updateDraftFilter('statusFilter', event.target.value)}
                  className="border-input bg-background h-9 min-w-0 rounded-md border px-3 text-sm"
                >
                  <option value="ALL">Tous</option>
                  <option value="OK">Opérationnel</option>
                  <option value="REVIEW_REQUIRED">Dégradé</option>
                </select>
              </FormField>
              <FormField label="Date de début">
                <Input
                  size="sm"
                  type="date"
                  value={draftFilters.dateFrom}
                  onChange={(event) => updateDraftFilter('dateFrom', event.target.value)}
                />
              </FormField>
              <FormField label="Date de fin">
                <Input
                  size="sm"
                  type="date"
                  value={draftFilters.dateTo}
                  onChange={(event) => updateDraftFilter('dateTo', event.target.value)}
                />
              </FormField>
              <div className="flex flex-wrap items-center justify-end gap-2 sm:col-span-2 lg:col-span-3">
                <Button type="submit" size="sm">
                  Rechercher
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={resetFilters}>
                  Réinitialiser
                </Button>
              </div>
            </form>
          }
          mobileCardLabel="Rapprochements"
          mobileCard={(batch) => (
            <DataCard
              eyebrow={batch.id}
              title={batch.period}
              description={`${batch.matchedCount} matchés · ${batch.unmatchedCount} écart(s)`}
              aside={<Amount value={batch.unmatchedAmount} />}
              meta={
                <>
                  {batch.status === 'OK' ? (
                    <StatusBadge kind="integration" status="UP" compact />
                  ) : (
                    <StatusBadge kind="integration" status="DEGRADED" compact />
                  )}
                  <span className="text-muted-foreground">{formatDateTime(batch.generatedAt)}</span>
                </>
              }
            />
          )}
          rowAction={{
            label: (batch) => `Consulter le rapprochement ${batch.period}`,
            onSelect: (batch) => {
              setSelectedBatchId(batch.id);
              setDetailSheetOpen(true);
            },
          }}
          emptyState={
            <EmptyState
              title="Aucun rapprochement dans cette vue"
              description="Modifiez le statut ou la plage de dates pour élargir le périmètre."
              action={
                <Button variant="outline" size="sm" onClick={resetFilters}>
                  Réinitialiser les filtres
                </Button>
              }
            />
          }
          pagination={{ label: `${filteredBatches.length} rapprochement(s)`, hasNextPage: false }}
        />
      </div>

      <Sheet open={detailSheetOpen} onOpenChange={setDetailSheetOpen}>
        <SheetContent className="w-[calc(100vw-2rem)] overflow-y-auto p-0 sm:max-w-xl">
          <SheetHeader className="border-border border-b p-5 pr-12">
            <SheetTitle>Détail du rapprochement</SheetTitle>
            <SheetDescription>
              {selectedBatch.period} · généré {formatDateTime(selectedBatch.generatedAt)}
            </SheetDescription>
          </SheetHeader>

          <div className="grid gap-4 p-5">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg border border-border p-4">
                <p className="text-muted-foreground text-xs uppercase">Montant rapproché</p>
                <div className="mt-2">
                  <Amount value={selectedBatch.matchedAmount} />
                </div>
                <p className="text-muted-foreground mt-1 text-sm">
                  {selectedBatch.matchedCount} transactions matchées.
                </p>
              </div>
              <div className="rounded-lg border border-border p-4">
                <p className="text-muted-foreground text-xs uppercase">Écart à revoir</p>
                <div className="mt-2">
                  <Amount value={selectedBatch.unmatchedAmount} />
                </div>
                <p className="text-muted-foreground mt-1 text-sm">
                  {selectedBatchDiscrepancies.length} paiement(s), {selectedBatch.unmatchedCount} ligne(s) en écart.
                </p>
              </div>
            </div>

            <div className="rounded-lg border border-border p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-muted-foreground text-xs uppercase">Statut</p>
                  <p className="text-quipux-navy-950 mt-1 font-medium">
                    {selectedBatch.status === 'OK' ? 'Rapprochement complet' : 'Écarts à traiter'}
                  </p>
                </div>
                {selectedBatch.status === 'OK' ? (
                  <StatusBadge kind="integration" status="UP" compact />
                ) : (
                  <StatusBadge kind="integration" status="DEGRADED" compact />
                )}
              </div>
              <p className="text-muted-foreground mt-3 text-sm">
                {selectedBatch.status === 'OK'
                  ? 'Aucun écart financier détecté sur cette période.'
                  : 'Les paiements ci-dessous doivent être analysés ou relancés depuis leur fiche.'}
              </p>
            </div>

            <section className="grid gap-3">
              <SectionHeader
                title="Paiements à revoir"
                description={`${selectedBatchDiscrepancies.length} paiement(s) à analyser pour cette période.`}
              />
              {selectedBatchDiscrepancies.length > 0 ? (
                <>
                  {/* Annotation / justification obligatoire avant clôture d'un écart (C-22). */}
                  <FormField label="Annotation / justification">
                    <ReasonTextarea
                      value={annotation}
                      onChange={(event) => setAnnotation(event.target.value)}
                      placeholder="Justifier la clôture des écarts de cette période…"
                    />
                  </FormField>
                  <div className="grid gap-3">
                    {selectedBatchDiscrepancies.map((payment) => (
                      <DataCard
                        key={payment.id}
                        eyebrow={payment.merchantReference}
                        title={payment.plate}
                        description={`${operatorLabel(payment.operatorCode)} · ${payment.lineItems.length} ligne(s) de paiement`}
                        aside={<Amount value={payment.totalAmount} />}
                        meta={<StatusBadge kind="sgi" status={payment.sgiStatus} compact />}
                        footer={
                          <div className="flex flex-wrap gap-2">
                            <Button asChild variant="outline" size="sm">
                              <Link to={`/payments/${payment.id}`}>Ouvrir le paiement</Link>
                            </Button>
                            {/* Clôture par écart, conditionnée à l'annotation (C-22). */}
                            <CapabilityGuard capability="reconciliation:run">
                              <Button
                                size="sm"
                                disabled={annotation.trim().length < 10}
                                onClick={() =>
                                  toast.success('Écart clôturé', {
                                    description: `${payment.merchantReference} — justification journalisée.`,
                                  })
                                }
                              >
                                <CheckCircle2 className="size-4" />
                                Clôturer l'écart
                              </Button>
                            </CapabilityGuard>
                          </div>
                        }
                      />
                    ))}
                  </div>
                </>
              ) : (
                <EmptyState
                  tone="positive"
                  title="Aucun écart"
                  description="Cette période ne contient aucun paiement à revoir."
                />
              )}
            </section>
          </div>

          <SheetFooter className="border-border border-t p-5">
            <Button onClick={() => downloadReconciliationCsv(selectedBatch, selectedBatchDiscrepancies)}>
              <Download className="size-4" />
              Télécharger CSV
            </Button>
            <SheetClose asChild>
              <Button variant="outline">Fermer</Button>
            </SheetClose>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}
